/**
 * Flow editor — A/V sync probe (timing instrumentation).
 *
 * Logs every SOUND TRIGGER (metronome tick, note hit SFX, scroll tick) and key
 * transport ACTION (play / pause / seek) with the AudioContext DSP clock
 * (`ctx`, the sample-accurate master clock) plus any drift the caller measures,
 * so we can pinpoint audible A/V drift. It complements the rate sampler in
 * FlowApp (Ctrl+Shift+D), which only tracks clock speed.
 *
 * Off by default. Toggled by the Sync debug console (Ctrl+Shift+D) or
 * `window.__flowProbe(true)` in the browser console. Dump the buffer with
 * `window.__flowProbeLog()`.
 *
 * Reading the lines:
 *   hit   ... driftMs = when the SFX fired MINUS when that note's audio is due.
 *             ~0 = tight; positive = SFX late; large/jumpy = frame jitter.
 *   metro ... leadMs  = how far ahead the click was scheduled (look-ahead).
 *   beat  ... driftMs = audio position when the VISUAL beat crossed MINUS the
 *             beat's true audio time (≈ syncOffset + up to one frame).
 *
 * No import from flowSounds (avoids an import cycle): the clock is injected via
 * setProbeClock(), registered lazily by flowSounds so no AudioContext is
 * created before a user gesture.
 */

let enabled = false;
let clock: () => number = () => 0; // returns AudioContext.currentTime
const ring: string[] = [];
const MAX = 6000;

/** flowSounds registers the AudioContext clock here (called lazily). */
export function setProbeClock(fn: () => number): void { clock = fn; }

export function setSyncProbeEnabled(on: boolean): void {
  if (on === enabled) return;
  enabled = on;
  // eslint-disable-next-line no-console
  console.log('[flow-probe]', on ? '────────── ENABLED ──────────' : '────────── disabled ──────────');
}
export function isSyncProbeEnabled(): boolean { return enabled; }
export function getSyncProbeLog(): string[] { return ring.slice(); }
export function clearSyncProbeLog(): void { ring.length = 0; }

function fmt(v: number | string | boolean): string {
  if (typeof v !== 'number') return String(v);
  if (Number.isInteger(v)) return String(v);
  return v.toFixed(Math.abs(v) >= 100 ? 2 : 4);
}

/**
 * Record one event. No-op unless the probe is enabled. `fields` are appended as
 * key=value pairs; numbers are formatted compactly.
 */
export function probe(tag: string, fields?: Record<string, number | string | boolean>): void {
  if (!enabled) return;
  let ctx = 0;
  try { ctx = clock(); } catch { /* ctx not ready yet */ }
  const parts = [`ctx=${ctx.toFixed(4)}`];
  if (fields) for (const k of Object.keys(fields)) parts.push(`${k}=${fmt(fields[k])}`);
  const line = `${tag.padEnd(11)} ${parts.join('  ')}`;
  ring.push(line);
  if (ring.length > MAX) ring.shift();
  // eslint-disable-next-line no-console
  console.log('[flow-probe]', line);
}

// Browser-console convenience handles.
if (typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__flowProbe = (on?: boolean) => setSyncProbeEnabled(on !== false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__flowProbeLog = () => getSyncProbeLog().join('\n');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__flowProbeClear = () => clearSyncProbeLog();
}
